import { printBlockString } from './blockString.mjs';
import { printString } from './printString.mjs';
import { visit } from './visitor.mjs';
/**
 * Converts an AST into a string, using one set of reasonable
 * formatting rules.
 */

export function print(ast) {
  return visit(ast, printDocASTReducer);
}
const MAX_LINE_LENGTH = 80;
const printDocASTReducer = {
  Name: {
    leave: node => node.value
  },
  Variable: {
    leave: node => '$' + node.name
  },
  // Document
  Document: {
    leave: node => join(node.definitions, '\n\n')
  },
  OperationDefinition: {
    leave(node) {
      const varDefs = wrap('(', join(node.variableDefinitions, ', '), ')');
      const prefix = join([node.operation, join([node.name, varDefs]), join(node.directives, ' ')], ' '); // Anonymous queries with no directives or variable definitions can use
      // the query short form.

      return (prefix === 'query' ? '' : prefix + ' ') + node.selectionSet;
    }
  },
  VariableDefinition: {
    leave: _ref => {
      let {
        variable,
        type,
        defaultValue,
        directives
      } = _ref;
      return variable + ': ' + type + wrap(' = ', defaultValue) + wrap(' ', join(directives, ' '));
    }
  },
  SelectionSet: {
    leave: _ref2 => {
      let {
        selections
      } = _ref2;
      return block(selections);
    }
  },
  Field: {
    leave(_ref3) {
      let {
        alias,
        name,
        arguments: args,
        directives,
        selectionSet
      } = _ref3;
      const prefix = wrap('', alias, ': ') + name;
      let argsLine = prefix + wrap('(', join(args, ', '), ')');
      if (argsLine.length > MAX_LINE_LENGTH) {
        argsLine = prefix + wrap('(\n', indent(join(args, '\n')), '\n)');
      }
      return join([argsLine, join(directives, ' '), selectionSet], ' ');
    }
  },
  Argument: {
    leave: _ref4 => {
      let {
        name,
        value
      } = _ref4;
      return name + ': ' + value;
    }
  },
  // Fragments
  FragmentSpread: {
    leave: _ref5 => {
      let {
        name,
        directives
      } = _ref5;
      return '...' + name + wrap(' ', join(directives, ' '));
    }
  },
  InlineFragment: {
    leave: _ref6 => {
      let {
        typeCondition,
        directives,
        selectionSet
      } = _ref6;
      return join(['...', wrap('on ', typeCondition), join(directives, ' '), selectionSet], ' ');
    }
  },
  FragmentDefinition: {
    leave: (_ref7 // Note: fragment variable definitions are experimental and may be changed
    ) => {
      let {
        name,
        typeCondition,
        variableDefinitions,
        directives,
        selectionSet
      } = _ref7;
      return (
        // or removed in the future.
        `fragment ${name}${wrap('(', join(variableDefinitions, ', '), ')')} ` + `on ${typeCondition} ${wrap('', join(directives, ' '), ' ')}` + selectionSet
      );
    }
  },
  // Value
  IntValue: {
    leave: _ref8 => {
      let {
        value
      } = _ref8;
      return value;
    }
  },
  FloatValue: {
    leave: _ref9 => {
      let {
        value
      } = _ref9;
      return value;
    }
  },
  StringValue: {
    leave: _ref0 => {
      let {
        value,
        block: isBlockString
      } = _ref0;
      return isBlockString ? printBlockString(value) : printString(value);
    }
  },
  BooleanValue: {
    leave: _ref1 => {
      let {
        value
      } = _ref1;
      return value ? 'true' : 'false';
    }
  },
  NullValue: {
    leave: () => 'null'
  },
  EnumValue: {
    leave: _ref10 => {
      let {
        value
      } = _ref10;
      return value;
    }
  },
  ListValue: {
    leave: _ref11 => {
      let {
        values
      } = _ref11;
      return '[' + join(values, ', ') + ']';
    }
  },
  ObjectValue: {
    leave: _ref12 => {
      let {
        fields
      } = _ref12;
      return '{' + join(fields, ', ') + '}';
    }
  },
  ObjectField: {
    leave: _ref13 => {
      let {
        name,
        value
      } = _ref13;
      return name + ': ' + value;
    }
  },
  // Directive
  Directive: {
    leave: _ref14 => {
      let {
        name,
        arguments: args
      } = _ref14;
      return '@' + name + wrap('(', join(args, ', '), ')');
    }
  },
  // Type
  NamedType: {
    leave: _ref15 => {
      let {
        name
      } = _ref15;
      return name;
    }
  },
  ListType: {
    leave: _ref16 => {
      let {
        type
      } = _ref16;
      return '[' + type + ']';
    }
  },
  NonNullType: {
    leave: _ref17 => {
      let {
        type
      } = _ref17;
      return type + '!';
    }
  },
  // Type System Definitions
  SchemaDefinition: {
    leave: _ref18 => {
      let {
        description,
        directives,
        operationTypes
      } = _ref18;
      return wrap('', description, '\n') + join(['schema', join(directives, ' '), block(operationTypes)], ' ');
    }
  },
  OperationTypeDefinition: {
    leave: _ref19 => {
      let {
        operation,
        type
      } = _ref19;
      return operation + ': ' + type;
    }
  },
  ScalarTypeDefinition: {
    leave: _ref20 => {
      let {
        description,
        name,
        directives
      } = _ref20;
      return wrap('', description, '\n') + join(['scalar', name, join(directives, ' ')], ' ');
    }
  },
  ObjectTypeDefinition: {
    leave: _ref21 => {
      let {
        description,
        name,
        interfaces,
        directives,
        fields
      } = _ref21;
      return wrap('', description, '\n') + join(['type', name, wrap('implements ', join(interfaces, ' & ')), join(directives, ' '), block(fields)], ' ');
    }
  },
  FieldDefinition: {
    leave: _ref22 => {
      let {
        description,
        name,
        arguments: args,
        type,
        directives
      } = _ref22;
      return wrap('', description, '\n') + name + (hasMultilineItems(args) ? wrap('(\n', indent(join(args, '\n')), '\n)') : wrap('(', join(args, ', '), ')')) + ': ' + type + wrap(' ', join(directives, ' '));
    }
  },
  InputValueDefinition: {
    leave: _ref23 => {
      let {
        description,
        name,
        type,
        defaultValue,
        directives
      } = _ref23;
      return wrap('', description, '\n') + join([name + ': ' + type, wrap('= ', defaultValue), join(directives, ' ')], ' ');
    }
  },
  InterfaceTypeDefinition: {
    leave: _ref24 => {
      let {
        description,
        name,
        interfaces,
        directives,
        fields
      } = _ref24;
      return wrap('', description, '\n') + join(['interface', name, wrap('implements ', join(interfaces, ' & ')), join(directives, ' '), block(fields)], ' ');
    }
  },
  UnionTypeDefinition: {
    leave: _ref25 => {
      let {
        description,
        name,
        directives,
        types
      } = _ref25;
      return wrap('', description, '\n') + join(['union', name, join(directives, ' '), wrap('= ', join(types, ' | '))], ' ');
    }
  },
  EnumTypeDefinition: {
    leave: _ref26 => {
      let {
        description,
        name,
        directives,
        values
      } = _ref26;
      return wrap('', description, '\n') + join(['enum', name, join(directives, ' '), block(values)], ' ');
    }
  },
  EnumValueDefinition: {
    leave: _ref27 => {
      let {
        description,
        name,
        directives
      } = _ref27;
      return wrap('', description, '\n') + join([name, join(directives, ' ')], ' ');
    }
  },
  InputObjectTypeDefinition: {
    leave: _ref28 => {
      let {
        description,
        name,
        directives,
        fields
      } = _ref28;
      return wrap('', description, '\n') + join(['input', name, join(directives, ' '), block(fields)], ' ');
    }
  },
  DirectiveDefinition: {
    leave: _ref29 => {
      let {
        description,
        name,
        arguments: args,
        repeatable,
        locations
      } = _ref29;
      return wrap('', description, '\n') + 'directive @' + name + (hasMultilineItems(args) ? wrap('(\n', indent(join(args, '\n')), '\n)') : wrap('(', join(args, ', '), ')')) + (repeatable ? ' repeatable' : '') + ' on ' + join(locations, ' | ');
    }
  },
  SchemaExtension: {
    leave: _ref30 => {
      let {
        directives,
        operationTypes
      } = _ref30;
      return join(['extend schema', join(directives, ' '), block(operationTypes)], ' ');
    }
  },
  ScalarTypeExtension: {
    leave: _ref31 => {
      let {
        name,
        directives
      } = _ref31;
      return join(['extend scalar', name, join(directives, ' ')], ' ');
    }
  },
  ObjectTypeExtension: {
    leave: _ref32 => {
      let {
        name,
        interfaces,
        directives,
        fields
      } = _ref32;
      return join(['extend type', name, wrap('implements ', join(interfaces, ' & ')), join(directives, ' '), block(fields)], ' ');
    }
  },
  InterfaceTypeExtension: {
    leave: _ref33 => {
      let {
        name,
        interfaces,
        directives,
        fields
      } = _ref33;
      return join(['extend interface', name, wrap('implements ', join(interfaces, ' & ')), join(directives, ' '), block(fields)], ' ');
    }
  },
  UnionTypeExtension: {
    leave: _ref34 => {
      let {
        name,
        directives,
        types
      } = _ref34;
      return join(['extend union', name, join(directives, ' '), wrap('= ', join(types, ' | '))], ' ');
    }
  },
  EnumTypeExtension: {
    leave: _ref35 => {
      let {
        name,
        directives,
        values
      } = _ref35;
      return join(['extend enum', name, join(directives, ' '), block(values)], ' ');
    }
  },
  InputObjectTypeExtension: {
    leave: _ref36 => {
      let {
        name,
        directives,
        fields
      } = _ref36;
      return join(['extend input', name, join(directives, ' '), block(fields)], ' ');
    }
  }
};
/**
 * Given maybeArray, print an empty string if it is null or empty, otherwise
 * print all items together separated by separator if provided
 */

function join(maybeArray) {
  let separator = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : '';
  var _maybeArray$filter$jo;
  return (_maybeArray$filter$jo = maybeArray === null || maybeArray === void 0 ? void 0 : maybeArray.filter(x => x).join(separator)) !== null && _maybeArray$filter$jo !== void 0 ? _maybeArray$filter$jo : '';
}
/**
 * Given array, print each item on its own line, wrapped in an indented `{ }` block.
 */

function block(array) {
  return wrap('{\n', indent(join(array, '\n')), '\n}');
}
/**
 * If maybeString is not null or empty, then wrap with start and end, otherwise print an empty string.
 */

function wrap(start, maybeString) {
  let end = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : '';
  return maybeString != null && maybeString !== '' ? start + maybeString + end : '';
}
function indent(str) {
  return wrap('  ', str.replace(/\n/g, '\n  '));
}
function hasMultilineItems(maybeArray) {
  var _maybeArray$some;

  // FIXME: https://github.com/graphql/graphql-js/issues/2203

  /* c8 ignore next */
  return (_maybeArray$some = maybeArray === null || maybeArray === void 0 ? void 0 : maybeArray.some(str => str.includes('\n'))) !== null && _maybeArray$some !== void 0 ? _maybeArray$some : false;
}